import React from 'react';
import ReactMarkdown from 'react-markdown';
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis
} from 'recharts';
import remarkGfm from 'remark-gfm';

// --- Chart Renderer with Type Mapping ---
const chartTypeMap: Record<string, (spec: any) => JSX.Element> = {
  LineChart: spec => (
    <LineChart width={400} height={250} data={spec.data}>
      <CartesianGrid stroke="#ccc" />
      <XAxis dataKey={spec.xKey} />
      <YAxis />
      <Tooltip />
      {spec.lines?.map((line: any, i: number) => (
        <Line key={i} type="monotone" dataKey={line.dataKey} stroke={line.stroke || '#8884d8'} />
      ))}
    </LineChart>
  ),
  BarChart: spec => (
    <BarChart width={400} height={250} data={spec.data}>
      <CartesianGrid stroke="#ccc" />
      <XAxis dataKey={spec.xKey} />
      <YAxis />
      <Tooltip />
      {spec.bars?.map((bar: any, i: number) => (
        <Bar key={i} dataKey={bar.dataKey} fill={bar.fill || '#82ca9d'} />
      ))}
    </BarChart>
  ),
  PieChart: spec => (
    <PieChart width={400} height={250}>
      <Tooltip />
      <Pie
        data={spec.data}
        dataKey={spec.dataKey}
        nameKey={spec.nameKey}
        cx="50%"
        cy="50%"
        outerRadius={80}
        fill="#8884d8"
        label
      >
        {spec.colors &&
          spec.data.map((entry: any, i: number) => (
            <Cell key={`cell-${i}`} fill={spec.colors[i % spec.colors.length]} />
          ))}
      </Pie>
    </PieChart>
  ),
  AreaChart: spec => (
    <AreaChart width={400} height={250} data={spec.data}>
      <CartesianGrid stroke="#ccc" />
      <XAxis dataKey={spec.xKey} />
      <YAxis />
      <Tooltip />
      {spec.areas?.map((area: any, i: number) => (
        <Area
          key={i}
          type="monotone"
          dataKey={area.dataKey}
          stroke={area.stroke || '#8884d8'}
          fill={area.fill || '#8884d8'}
        />
      ))}
    </AreaChart>
  ),
  ScatterChart: spec => (
    <ScatterChart width={400} height={250}>
      <CartesianGrid stroke="#ccc" />
      <XAxis dataKey={spec.xKey} name={spec.xName} />
      <YAxis dataKey={spec.yKey} name={spec.yName} />
      {spec.zKey && <ZAxis dataKey={spec.zKey} range={[60, 400]} />}
      <Tooltip cursor={{ strokeDasharray: '3 3' }} />
      <Scatter name={spec.scatterName || 'Data'} data={spec.data} fill={spec.fill || '#8884d8'} />
    </ScatterChart>
  )
};

function ChartRenderer({ spec }: { spec: any }) {
  if (spec && typeof spec.type === 'string' && chartTypeMap[spec.type]) {
    return chartTypeMap[spec.type](spec);
  }
  return <pre>Unsupported chart type</pre>;
}

export default function ChatMessage({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ node, inline, className, children, ...props }) {
          const lang = (className || '').replace('language-', '');
          const raw = String(children).trim();

          if (!inline && lang === 'chart') {
            try {
              const json = JSON.parse(raw);
              return <ChartRenderer spec={json} />;
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Failed to parse chart JSON:', err);
              return <pre className="bg-gray-100 p-3 rounded">{raw}</pre>;
            }
          }

          return <code {...props}>{children}</code>;
        },
        table({ node, ...props }) {
          return (
            <div className="chat-table-wrapper">
              <table {...props} />
            </div>
          );
        }
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
